import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { FarmingResultsProduct, FarmingMetrics, ProcessedFarmingMetrics } from '../types/farmingResultsTypes';
import { parseNumericValue } from './pmt2Utils';

/**
 * Service for handling Farming Results data operations
 * Reuses PMT2 infrastructure but focuses on farming-specific metrics
 */
export const farmingResultsService = {
  
  /**
   * Get companies that have farming data
   */
  getCompanies: async (): Promise<string[]> => {
    try {
      const companiesSet = new Set<string>();
      
      // Get all companies from pmt2_data collection
      const dataCollection = collection(db, 'pmt2_data');
      const snapshot = await getDocs(dataCollection);
      
      snapshot.docs.forEach(doc => {
        const company = doc.id;
        companiesSet.add(company);
      });
      
      return Array.from(companiesSet).sort();
    } catch (error) {
      console.error('Error getting companies:', error);
      throw new Error('Failed to load companies');
    }
  },

  /**
   * Get products for a specific company
   */
  getProductsByCompany: async (companyName: string): Promise<FarmingResultsProduct[]> => {
    try {
      const productsCollection = collection(db, `pmt2_data/${companyName}/products`);
      const snapshot = await getDocs(productsCollection);
      
      const products: FarmingResultsProduct[] = [];
      
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        products.push({
          id: doc.id,
          companyName,
          ...data
        } as FarmingResultsProduct);
      });
      
      // Sort products by title or ASIN
      return products.sort((a, b) => {
        const aTitle = a.Title || a.ASIN || a.id;
        const bTitle = b.Title || b.ASIN || b.id;
        return aTitle.localeCompare(bTitle);
      });
    } catch (error) {
      console.error(`Error getting products for company ${companyName}:`, error);
      throw new Error(`Failed to load products for ${companyName}`);
    }
  },

  /**
   * Get farming metrics for a specific product
   */
  getFarmingMetrics: async (companyName: string, asin: string): Promise<FarmingMetrics | null> => {
    try {
      const productDoc = doc(db, `pmt2_data/${companyName}/products/${asin}`);
      const snapshot = await getDoc(productDoc);
      
      if (!snapshot.exists()) {
        return null;
      }
      
      const data = snapshot.data();
      
      // Extract only the farming-related fields
      const farmingMetrics: FarmingMetrics = {
        'Time Farming, if Actively Farming': data['Time Farming, if Actively Farming'],
        'Gross Profit': data['Gross Profit'],
        'Final Gross Profit': data['Final Gross Profit'],
        'Gross Profit Margin': data['Gross Profit Margin'],
        'Final Target ACoS': data['Final Target ACoS'],
        'Farming Cost Per New CST Override': data['Farming Cost Per New CST Override'],
        'Final Farming Cost Per New CST': data['Final Farming Cost Per New CST'],
        'Total Inventory': data['Total Inventory'],
        'Days of Cover': data['Days of Cover'],
        'Broad': data['Broad'],
        'CTA': data['CTA'],
        'Exact': data['Exact'],
        'PTA': data['PTA'],
        'Constrain Farming Campaigns to the Max Bid': data['Constrain Farming Campaigns to the Max Bid']
      };
      
      return farmingMetrics;
    } catch (error) {
      console.error(`Error getting farming metrics for ${companyName}/${asin}:`, error);
      throw new Error(`Failed to load farming metrics for ${asin}`);
    }
  },

  /**
   * Process raw farming metrics into display format
   */
  processFarmingMetrics: (rawMetrics: FarmingMetrics): ProcessedFarmingMetrics => {
    const formatCurrency = (value: any): string => {
      const num = parseNumericValue(value);
      if (num === null) return '-';
      return `$${num.toFixed(2)}`;
    };

    const formatPercentage = (value: any): string => {
      const num = parseNumericValue(value);
      if (num === null) return '-';
      return `${Math.round(num)}%`;
    };

    const formatNumber = (value: any): number | null => {
      return parseNumericValue(value);
    };

    const formatString = (value: any): string => {
      return value && value.toString().trim() ? value.toString() : '-';
    };

    // Calculate Default $/CST (Gross Profit * 1.5)
    const grossProfit = parseNumericValue(rawMetrics['Gross Profit']);
    const defaultCostPerCST = grossProfit !== null ? grossProfit * 1.5 : null;

    return {
      daysFarming: formatNumber(rawMetrics['Time Farming, if Actively Farming']),
      actualGP: formatCurrency(rawMetrics['Gross Profit']),
      finalGP: formatCurrency(rawMetrics['Final Gross Profit']),
      actualGPMargin: formatPercentage(rawMetrics['Gross Profit Margin']),
      finalGPMargin: formatPercentage(rawMetrics['Final Target ACoS']),
      defaultCostPerCST: defaultCostPerCST !== null ? `$${defaultCostPerCST.toFixed(2)}` : '-',
      cstOverride: formatCurrency(rawMetrics['Farming Cost Per New CST Override']),
      finalCostPerCST: formatCurrency(rawMetrics['Final Farming Cost Per New CST']),
      limitFarmingToMaxBid: formatString(rawMetrics['Constrain Farming Campaigns to the Max Bid']),
      totalInventory: formatNumber(rawMetrics['Total Inventory']),
      daysOfCover: formatNumber(rawMetrics['Days of Cover']),
      broadSeeds: formatNumber(rawMetrics['Broad']),
      ctaTargets: formatNumber(rawMetrics['CTA']),
      exactTargets: formatNumber(rawMetrics['Exact']),
      ptaTargets: formatNumber(rawMetrics['PTA'])
    };
  },

  /**
   * Get processed farming metrics for display
   */
  getProcessedFarmingMetrics: async (companyName: string, asin: string): Promise<ProcessedFarmingMetrics | null> => {
    try {
      const rawMetrics = await farmingResultsService.getFarmingMetrics(companyName, asin);
      if (!rawMetrics) return null;
      
      return farmingResultsService.processFarmingMetrics(rawMetrics);
    } catch (error) {
      console.error(`Error getting processed farming metrics for ${companyName}/${asin}:`, error);
      throw new Error(`Failed to process farming metrics for ${asin}`);
    }
  }
};